import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, Select, DatePicker, Form, Input, Modal,
  message, Typography, Tooltip, Descriptions, Row, Col,
} from 'antd';
import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  getAuditLog, overrideVaultBalance, overrideCaseInventoryStatus, signOffReconciliation,
} from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const ACTION_COLORS = {
  MANUAL_VAULT_ADJUSTMENT: 'orange',
  TRANSFER_OVERRIDE: 'purple',
  BATCH_OVERRIDE: 'blue',
  RECONCILIATION_SIGN_OFF: 'green',
  CASE_STATUS_OVERRIDE: 'red',
};

export default function VaultAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({});
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideForm] = Form.useForm();
  const [overrideLoading, setOverrideLoading] = useState(false);

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const resp = await getAuditLog({ page, limit: 20, ...filters });
      setLogs(resp.data || []);
      setMeta(resp.meta || { total: 0, page: 1, pages: 1 });
    } catch {
      message.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  const handleOverrideSubmit = async () => {
    try {
      const values = await overrideForm.validateFields();
      setOverrideLoading(true);

      if (overrideModal === 'vault') {
        await overrideVaultBalance(values);
        message.success('Vault balance adjusted');
      } else if (overrideModal === 'case') {
        await overrideCaseInventoryStatus(values);
        message.success('Case status overridden');
      } else if (overrideModal === 'reconciliation') {
        await signOffReconciliation(values);
        message.success('Reconciliation signed off');
      }

      setOverrideModal(null);
      overrideForm.resetFields();
      fetchLogs(1);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || err.message || 'Override failed');
    } finally {
      setOverrideLoading(false);
    }
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      render: (v) => new Date(v).toLocaleString('en-IN'),
      width: 170,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (v) => (
        <Tag color={ACTION_COLORS[v] || 'default'} style={{ fontSize: 11 }}>
          {v.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: 'Target',
      dataIndex: 'targetModel',
      render: (model, row) => (
        <span>
          <Text type="secondary" style={{ fontSize: 11 }}>{model}</Text>
          {row.targetRef && <><br /><Text strong style={{ fontSize: 12 }}>{row.targetRef}</Text></>}
        </span>
      ),
    },
    {
      title: 'Previous → New',
      render: (_, row) => (
        <Tooltip
          title={
            <div>
              <div><strong>Before:</strong> {JSON.stringify(row.previousValue)}</div>
              <div><strong>After:</strong> {JSON.stringify(row.newValue)}</div>
            </div>
          }
        >
          <Text style={{ fontSize: 11, cursor: 'pointer', color: '#1677ff' }}>View change</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      ellipsis: true,
    },
    {
      title: 'Performed By',
      dataIndex: ['performedBy', 'name'],
      render: (v) => v || '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Vault Audit Log</Title>
          <Text type="secondary">Admin overrides and manual adjustments</Text>
        </Col>
        <Col>
          <Space>
            <Button onClick={() => { overrideForm.resetFields(); setOverrideModal('vault'); }}>
              Adjust Vault Balance
            </Button>
            <Button onClick={() => { overrideForm.resetFields(); setOverrideModal('case'); }}>
              Override Case Status
            </Button>
            <Button type="primary" onClick={() => { overrideForm.resetFields(); setOverrideModal('reconciliation'); }}>
              Sign Off Reconciliation
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Action"
            allowClear
            style={{ width: 220 }}
            onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
            options={[
              { value: 'MANUAL_VAULT_ADJUSTMENT', label: 'Manual Vault Adjustment' },
              { value: 'TRANSFER_OVERRIDE', label: 'Transfer Override' },
              { value: 'BATCH_OVERRIDE', label: 'Batch Override' },
              { value: 'RECONCILIATION_SIGN_OFF', label: 'Reconciliation Sign-Off' },
              { value: 'CASE_STATUS_OVERRIDE', label: 'Case Status Override' },
            ]}
          />
          <Select
            placeholder="Target Model"
            allowClear
            style={{ width: 180 }}
            onChange={(v) => setFilters((f) => ({ ...f, targetModel: v }))}
            options={[
              { value: 'BranchVault', label: 'Branch Vault' },
              { value: 'HOVault', label: 'HO Vault' },
              { value: 'InventoryTransfer', label: 'Transfer' },
              { value: 'GoldInventoryBatch', label: 'Batch' },
              { value: 'WalkInPurchaseCase', label: 'Walk-In Case' },
              { value: 'PledgeReleaseCase', label: 'Pledge Case' },
            ]}
          />
          <RangePicker
            onChange={(dates) => setFilters((f) => ({
              ...f,
              from: dates?.[0]?.toISOString(),
              to: dates?.[1]?.toISOString(),
            }))}
          />
          <Button icon={<SearchOutlined />} onClick={() => fetchLogs(1)}>Search</Button>
        </Space>
      </Card>

      <Table
        dataSource={logs}
        columns={columns}
        loading={loading}
        rowKey="_id"
        size="small"
        pagination={{
          total: meta.total,
          pageSize: 20,
          current: meta.page,
          onChange: (p) => fetchLogs(p),
          showTotal: (t) => `${t} entries`,
        }}
      />

      {/* Override Modal */}
      <Modal
        open={!!overrideModal}
        title={
          overrideModal === 'vault' ? 'Adjust Vault Balance'
            : overrideModal === 'case' ? 'Override Case Inventory Status'
            : 'Sign Off Reconciliation'
        }
        onCancel={() => { setOverrideModal(null); overrideForm.resetFields(); }}
        onOk={handleOverrideSubmit}
        confirmLoading={overrideLoading}
        okText="Confirm Override"
        okButtonProps={{ danger: true }}
      >
        <Form form={overrideForm} layout="vertical">
          {overrideModal === 'vault' && (
            <>
              <Form.Item name="vaultType" label="Vault Type" rules={[{ required: true }]}>
                <Select options={[{ value: 'BRANCH', label: 'Branch Vault' }, { value: 'HO', label: 'HO Vault' }]} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.vaultType !== curr.vaultType}>
                {({ getFieldValue }) =>
                  getFieldValue('vaultType') === 'BRANCH' && (
                    <Form.Item name="branchId" label="Branch ID" rules={[{ required: true }]}>
                      <Input placeholder="MongoDB ObjectId of branch" />
                    </Form.Item>
                  )
                }
              </Form.Item>
              <Form.Item name="grossWeightAdjustment" label="Gross Weight Adjustment (g)" rules={[{ required: true }]}>
                <Input type="number" step="0.001" placeholder="+5.000 or -2.500" />
              </Form.Item>
              <Form.Item name="netWeightAdjustment" label="Net Weight Adjustment (g)" rules={[{ required: true }]}>
                <Input type="number" step="0.001" placeholder="+5.000 or -2.500" />
              </Form.Item>
            </>
          )}

          {overrideModal === 'case' && (
            <>
              <Form.Item name="caseType" label="Case Type" rules={[{ required: true }]}>
                <Select options={[{ value: 'walkin', label: 'Walk-In Purchase' }, { value: 'pledge', label: 'Pledge Release' }]} />
              </Form.Item>
              <Form.Item name="caseId" label="Case ID (MongoDB _id)" rules={[{ required: true }]}>
                <Input placeholder="MongoDB ObjectId" />
              </Form.Item>
              <Form.Item name="newInventoryStatus" label="New Inventory Status" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'PENDING', label: 'PENDING' },
                  { value: 'IN_BRANCH_VAULT', label: 'IN_BRANCH_VAULT' },
                  { value: 'IN_TRANSFER', label: 'IN_TRANSFER' },
                  { value: 'IN_HO_VAULT', label: 'IN_HO_VAULT' },
                  { value: 'BATCHED', label: 'BATCHED' },
                ]} />
              </Form.Item>
            </>
          )}

          {overrideModal === 'reconciliation' && (
            <>
              <Form.Item name="vaultType" label="Vault Type" rules={[{ required: true }]}>
                <Select options={[{ value: 'BRANCH', label: 'Branch Vault' }, { value: 'HO', label: 'HO Vault' }]} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.vaultType !== curr.vaultType}>
                {({ getFieldValue }) =>
                  getFieldValue('vaultType') === 'BRANCH' && (
                    <Form.Item name="branchId" label="Branch ID" rules={[{ required: true }]}>
                      <Input placeholder="MongoDB ObjectId of branch" />
                    </Form.Item>
                  )
                }
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} placeholder="Reconciliation sign-off notes" />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="reason"
            label="Reason (minimum 10 characters)"
            rules={[{ required: true, min: 10 }]}
          >
            <Input.TextArea rows={3} placeholder="Explain why this override is necessary" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
